import {
  GreyGrey10,
  GreyGrey30,
  ShadowXs,
  Box,
  Flex
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import styled, { keyframes } from 'styled-components'

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`

const LAYOUT_WIDTHS = {
  leftSection: 400,
  customerColumn: 200,
  totalColumn: 200,
  rightSection: 250,
  recognizedColumn: 125,
  remainingColumn: 125,
  monthColumn: 120
} as const

const SKELETON_ROW_COUNT = 15
const ROW_HEIGHT = 32

type Props = {
  asOfDate: string
  monthCount?: number
}

const SkeletonBox = styled.div<{ $width: string }>`
  height: 16px;
  width: ${props => props.$width};
  border-radius: 4px;
  background-color: ${GreyGrey10};
  background-image: linear-gradient(
    90deg,
    ${GreyGrey10} 0%,
    ${GreyGrey30} 50%,
    ${GreyGrey10} 100%
  );
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite linear;
`

export const RevenueWaterfallTableSkeleton = ({
  asOfDate,
  monthCount = 12
}: Props) => {
  // Generate mock month columns
  const monthlyColumns = Array.from({ length: monthCount }, (_, i) => {
    const date = new Date()
    date.setMonth(date.getMonth() + i)
    return format(date, 'MMM yyyy')
  })

  return (
    <Box
      bg="white"
      position="relative"
      borderRadius="8px"
      w="full"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      overflow="hidden"
      maxHeight="calc(100vh - 126px)"
      display="flex"
      flexDirection="column"
    >
      {/* Top header group */}
      <Flex position="sticky" top={0} zIndex={23} bg="white" flexShrink={0}>
        <Box width={`${LAYOUT_WIDTHS.leftSection}px`} flexShrink={0}>
          <Box
            height="32px"
            borderBottom={`1px solid ${GreyGrey30}`}
            borderRight={`1px solid ${GreyGrey30}`}
            bg={GreyGrey10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            fontWeight="semibold"
            color="#4A5568"
          >
            Booked
          </Box>
        </Box>
        <Box flex={1}>
          <Box
            height="32px"
            borderBottom={`1px solid ${GreyGrey30}`}
            bg={GreyGrey10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            fontWeight="semibold"
            color="#4A5568"
          >
            Recognized
          </Box>
        </Box>
        <Box width={`${LAYOUT_WIDTHS.rightSection}px`} flexShrink={0}>
          <Box
            height="32px"
            borderBottom={`1px solid ${GreyGrey30}`}
            borderLeft={`1px solid ${GreyGrey30}`}
            bg={GreyGrey10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            fontWeight="semibold"
            color="#4A5568"
          >
            As of {format(asOfDate, 'MMMM yyyy')}
          </Box>
        </Box>
      </Flex>

      {/* Column headers */}
      <Flex
        position="sticky"
        top="0px"
        zIndex={22}
        bg="white"
        flexShrink={0}
        minWidth={`${LAYOUT_WIDTHS.leftSection + LAYOUT_WIDTHS.rightSection + monthCount * LAYOUT_WIDTHS.monthColumn}px`}
      >
        {/* Left frozen headers */}
        <Flex
          position="sticky"
          left={0}
          zIndex={21}
          bg="white"
          width={`${LAYOUT_WIDTHS.leftSection}px`}
          flexShrink={0}
        >
          <Box width={`${LAYOUT_WIDTHS.customerColumn}px`}>
            <Box
              height="32px"
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              padding="8px 12px"
              fontSize="12px"
              fontWeight="semibold"
              color="#718096"
              borderRight={`1px solid ${GreyGrey30}`}
              borderBottom={`1px solid ${GreyGrey30}`}
            >
              Customers
            </Box>
          </Box>
          <Box width={`${LAYOUT_WIDTHS.totalColumn}px`}>
            <Box
              height="32px"
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              padding="8px 12px"
              fontSize="12px"
              fontWeight="semibold"
              color="#718096"
              borderRight={`1px solid ${GreyGrey30}`}
              borderBottom={`1px solid ${GreyGrey30}`}
            >
              Total
            </Box>
          </Box>
        </Flex>

        {/* Middle scrollable headers */}
        <Flex flex={1} minW={`${monthCount * LAYOUT_WIDTHS.monthColumn}px`}>
          {monthlyColumns.map((month, index) => (
            <Box key={month} width="100%">
              <Box
                height="32px"
                bg={GreyGrey10}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                padding="8px 12px"
                fontSize="12px"
                fontWeight="semibold"
                color="#718096"
                borderRight={
                  index === monthlyColumns.length - 1
                    ? 'none'
                    : `1px solid ${GreyGrey30}`
                }
                borderBottom={`1px solid ${GreyGrey30}`}
              >
                {month}
              </Box>
            </Box>
          ))}
        </Flex>

        {/* Right frozen headers */}
        <Flex
          position="sticky"
          right={0}
          zIndex={21}
          bg="white"
          width={`${LAYOUT_WIDTHS.rightSection}px`}
          flexShrink={0}
          borderLeft={`1px solid ${GreyGrey30}`}
        >
          <Box width={`${LAYOUT_WIDTHS.recognizedColumn}px`}>
            <Box
              height="32px"
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              padding="8px 12px"
              fontSize="12px"
              fontWeight="semibold"
              color="#718096"
              borderBottom={`1px solid ${GreyGrey30}`}
            >
              Recognized
            </Box>
          </Box>
          <Box width={`${LAYOUT_WIDTHS.remainingColumn}px`}>
            <Box
              height="32px"
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              padding="8px 12px"
              fontSize="12px"
              fontWeight="semibold"
              color="#718096"
              borderBottom={`1px solid ${GreyGrey30}`}
            >
              Remaining
            </Box>
          </Box>
        </Flex>
      </Flex>

      {/* Scrollable content area */}
      <Box flex={1} overflow="auto" position="relative">
        <Box
          position="relative"
          minWidth={`${LAYOUT_WIDTHS.leftSection + LAYOUT_WIDTHS.rightSection + monthCount * LAYOUT_WIDTHS.monthColumn}px`}
        >
          {/* Skeleton rows */}
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
            <Flex key={rowIndex} height={`${ROW_HEIGHT}px`} minWidth="100%">
              {/* Left section */}
              <Flex
                position="sticky"
                left={0}
                zIndex={20}
                bg="white"
                width={`${LAYOUT_WIDTHS.leftSection}px`}
                flexShrink={0}
              >
                <Box
                  width={`${LAYOUT_WIDTHS.customerColumn}px`}
                  display="flex"
                  alignItems="center"
                  padding="8px 12px"
                  borderTop={`1px solid ${GreyGrey30}`}
                  borderRight={`1px solid ${GreyGrey30}`}
                >
                  <SkeletonBox $width="70%" />
                </Box>
                <Box
                  width={`${LAYOUT_WIDTHS.totalColumn}px`}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  padding="8px 12px"
                  borderTop={`1px solid ${GreyGrey30}`}
                  borderRight={`1px solid ${GreyGrey30}`}
                >
                  <SkeletonBox $width="80px" />
                </Box>
              </Flex>

              {/* Middle section */}
              <Flex
                flex={1}
                minW={`${monthCount * LAYOUT_WIDTHS.monthColumn}px`}
              >
                {monthlyColumns.map((_, colIndex) => (
                  <Box
                    key={colIndex}
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    padding="8px 12px"
                    borderTop={`1px solid ${GreyGrey30}`}
                    borderRight={
                      colIndex === monthlyColumns.length - 1
                        ? 'none'
                        : `1px solid ${GreyGrey30}`
                    }
                  >
                    <SkeletonBox $width="70px" />
                  </Box>
                ))}
              </Flex>

              {/* Right section */}
              <Flex
                position="sticky"
                right={0}
                zIndex={20}
                bg="white"
                width={`${LAYOUT_WIDTHS.rightSection}px`}
                flexShrink={0}
                borderLeft={`1px solid ${GreyGrey30}`}
              >
                <Box
                  width={`${LAYOUT_WIDTHS.recognizedColumn}px`}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  padding="8px 12px"
                  borderTop={`1px solid ${GreyGrey30}`}
                >
                  <SkeletonBox $width="80px" />
                </Box>
                <Box
                  width={`${LAYOUT_WIDTHS.remainingColumn}px`}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  padding="8px 12px"
                  borderTop={`1px solid ${GreyGrey30}`}
                >
                  <SkeletonBox $width="80px" />
                </Box>
              </Flex>
            </Flex>
          ))}
        </Box>
      </Box>

      {/* Summary row */}
      <Flex
        position="sticky"
        bottom={0}
        zIndex={22}
        bg="white"
        minWidth={`${LAYOUT_WIDTHS.leftSection + LAYOUT_WIDTHS.rightSection + monthCount * LAYOUT_WIDTHS.monthColumn}px`}
      >
        {/* Left section */}
        <Flex
          position="sticky"
          left={0}
          zIndex={21}
          bg="white"
          width={`${LAYOUT_WIDTHS.leftSection}px`}
          flexShrink={0}
        >
          <Box width={`${LAYOUT_WIDTHS.leftSection}px`}>
            <Box
              height="32px"
              borderTop={`1px solid ${GreyGrey30}`}
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              padding="8px 12px"
              borderRight={`1px solid ${GreyGrey30}`}
            >
              <Box fontSize="12px" fontWeight="semibold" color="#4A5568">
                Total
              </Box>
              <SkeletonBox $width="90px" />
            </Box>
          </Box>
        </Flex>

        {/* Middle section */}
        <Flex flex={1} minW={`${monthCount * LAYOUT_WIDTHS.monthColumn}px`}>
          {monthlyColumns.map((_, index) => (
            <Box key={index} width="100%">
              <Box
                height="32px"
                borderTop={`1px solid ${GreyGrey30}`}
                bg={GreyGrey10}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                padding="8px 12px"
                borderRight={
                  index === monthlyColumns.length - 1
                    ? 'none'
                    : `1px solid ${GreyGrey30}`
                }
              >
                <SkeletonBox $width="80px" />
              </Box>
            </Box>
          ))}
        </Flex>

        {/* Right section */}
        <Flex
          position="sticky"
          right={0}
          zIndex={21}
          bg="white"
          width={`${LAYOUT_WIDTHS.rightSection}px`}
          flexShrink={0}
          borderLeft={`1px solid ${GreyGrey30}`}
        >
          <Box width={`${LAYOUT_WIDTHS.recognizedColumn}px`}>
            <Box
              height="32px"
              borderTop={`1px solid ${GreyGrey30}`}
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              padding="8px 12px"
            >
              <SkeletonBox $width="90px" />
            </Box>
          </Box>
          <Box width={`${LAYOUT_WIDTHS.remainingColumn}px`}>
            <Box
              height="32px"
              borderTop={`1px solid ${GreyGrey30}`}
              bg={GreyGrey10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              padding="8px 12px"
            >
              <SkeletonBox $width="90px" />
            </Box>
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}
